import Moment from 'moment';
import {
  ACCOUNT_CMAIL,
  ACTIVITY,
  AGENCY_ENDORSEMENTS,
  AGENCY_LEVELS,
  AGENCY_TEAM_LOCATION_MAPPING,
  CARRIER_RISK_PROFILE,
  CARRIER_USERS_TABLE_ID,
  COWBELL_AGENCY_USER_MIGRATIONS,
  COWBELL_BINDERS,
  COWBELL_ENDORSEMENTS,
  COWBELL_NOTES,
  COWBELL_POLICY,
  COWBELL_QUOTES,
  COWBELL_RISK_ENG,
  COWBELL_SUBMISSIONS_WORKSPACE,
  LOSS_RUNS_HISTORY,
  CLAIMS_BULLPEN_TABLE_ID,
} from '../components/tables/table_constants';
import {
  AGENCY_CLAIMS,
  CARRIER_CLAIMS,
  CLAIMS_DIARY,
  CLAIMS_INVOICES,
  CLAIMS_TASKS,
  COWBELL_CLAIMS,
  COWBELL_CLAIMS_INVOICES,
  COWBELL_LOSS_RATIO_GRAPH,
  VENDOR_CLAIMS,
} from '../console/claims/claims.constants';
import { RESERVES } from '../console/claims/detail/financials/reserves.constants';
import { COWBELL_EPOCH } from './date.utils';
import { CMAIL } from '../console/cmail/cmail.statics';

const DEFAULT_BACKFILL = {
  size: 25,
  order: 'desc',
  orderBy: 'modified',
};

export const useGetBackfill = (tableId?: string) => {
  switch (tableId) {
    case ACCOUNT_CMAIL:
      return {
        size: DEFAULT_BACKFILL.size,
        before: () => Moment().startOf('y').unix(),
        after: Moment('01-01-2020').unix(),
      };

    case ACTIVITY:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(1, 'week').startOf('day').unix(),
      };

    case AGENCY_ENDORSEMENTS:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(2, 'year').startOf('day').unix(),
      };

    case AGENCY_CLAIMS:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(3, 'month').startOf('day').unix(),
      };

    case AGENCY_LEVELS:
    case AGENCY_TEAM_LOCATION_MAPPING:
      return {
        size: DEFAULT_BACKFILL.size,
      };

    case CARRIER_CLAIMS:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(3, 'month').startOf('day').unix(),
      };

    case CARRIER_RISK_PROFILE:
      return {
        size: DEFAULT_BACKFILL.size,
        order: 'asc',
        orderBy: 'policyPrefix',
      };

    case CARRIER_USERS_TABLE_ID:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(6, 'month').startOf('day').unix(),
      };

    case CLAIMS_DIARY:
      return {
        ...DEFAULT_BACKFILL,
        orderBy: 'created',
      };

    case CLAIMS_INVOICES:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(1, 'years').startOf('day').unix(),
      };

    case CLAIMS_TASKS:
      return {
        orderBy: 'dueDate',
        order: 'asc',
      };

    case CMAIL:
      return {
        size: DEFAULT_BACKFILL.size,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(3, 'months').startOf('day').unix(),
      };

    case COWBELL_AGENCY_USER_MIGRATIONS:
      return {
        ...DEFAULT_BACKFILL,
        orderBy: 'created',
      };

    case COWBELL_BINDERS:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(1, 'year').startOf('day').unix(),
      };

    case COWBELL_CLAIMS:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment(COWBELL_EPOCH).startOf('day').unix(),
      };

    case CLAIMS_BULLPEN_TABLE_ID:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment(COWBELL_EPOCH).startOf('day').unix(),
      };

    case COWBELL_CLAIMS_INVOICES:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment(COWBELL_EPOCH).startOf('day').unix(),
      };

    case COWBELL_LOSS_RATIO_GRAPH:
      return {
        size: DEFAULT_BACKFILL.size,
        order: 'asc',
        orderBy: 'date',
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(1, 'year').startOf('day').unix(),
      };

    case COWBELL_ENDORSEMENTS:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(2, 'year').startOf('day').unix(),
      };

    case COWBELL_NOTES:
      return {
        ...DEFAULT_BACKFILL,
        orderBy: 'created',
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(1, 'year').startOf('day').unix(),
      };

    case COWBELL_SUBMISSIONS_WORKSPACE:
      return {
        ...DEFAULT_BACKFILL,
        // Not DEFAULT_BACKFILL's `modified`: this table renders no Modified
        // column, so that sort draws no arrow and reorders rows as processors
        // touch submissions. `createdDate` is the column titled "Created Date"
        // and maps to the server's `created` key via SORT_KEY_MAPPING.
        orderBy: 'createdDate',
        dateFilter: 'created',
        before: () => Moment().add(1, 'days').endOf('day').unix(),
        after: () => Moment().subtract(1, 'year').startOf('day').unix(),
      };

    case COWBELL_POLICY:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().add(1, 'days').endOf('day').unix(),
        after: () => Moment().subtract(2, 'year').startOf('day').unix(),
      };

    case COWBELL_QUOTES:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(6, 'month').startOf('day').unix(),
      };

    case COWBELL_RISK_ENG:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(6, 'month').startOf('day').unix(),
      };

    case LOSS_RUNS_HISTORY:
      return {
        size: DEFAULT_BACKFILL.size,
        order: 'asc',
        orderBy: 'created',
      };

    case RESERVES:
      return {
        size: DEFAULT_BACKFILL.size,
        dateFilter: 'created',
        order: 'desc',
        orderBy: 'created',
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(1, 'years').startOf('day').unix(),
      };

    case VENDOR_CLAIMS:
      return {
        ...DEFAULT_BACKFILL,
        before: () => Moment().endOf('day').unix(),
        after: () => Moment().subtract(3, 'month').startOf('day').unix(),
      };

    default:
      return DEFAULT_BACKFILL;
  }
};
